import { useState } from 'react';
import { Save, X } from 'lucide-react';
import type {
  ProductRecommendation,
  RecommendationPlacement,
  RecommendationTargetMode,
  RecommendationVariant,
  RecommendationPosition,
  PlacementSettings,
  PlacementSetting,
} from '@parentpath/supabase-client';
import { ImageUpload } from '@/components/ui/ImageUpload';
import {
  PLACEMENT_LABELS,
  PLACEMENT_OPTIONS,
  PLACEMENT_DEFAULTS,
  VARIANT_LABELS,
  POSITION_OPTIONS_BY_PLACEMENT,
} from './constants';

interface Props {
  recommendation?: ProductRecommendation;
  onSubmit: (data: Partial<ProductRecommendation>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

type DurationMode = 'end_date' | 'duration_days';

export function RecommendationForm({ recommendation, onSubmit, onCancel, isLoading, isEditing }: Props) {
  const [title, setTitle] = useState(recommendation?.title ?? '');
  const [description, setDescription] = useState(recommendation?.description ?? '');
  const [ctaLabel, setCtaLabel] = useState(recommendation?.cta_label ?? 'Mehr erfahren');
  const [label, setLabel] = useState(recommendation?.label ?? 'Anzeige');
  const [ctaUrl, setCtaUrl] = useState(recommendation?.cta_url ?? '');
  const [imageUrl, setImageUrl] = useState(recommendation?.image_url ?? '');
  const [showImage, setShowImage] = useState(recommendation?.show_image ?? true);
  const [placements, setPlacements] = useState<RecommendationPlacement[]>(
    recommendation?.placements ?? []
  );
  const [placementSettings, setPlacementSettings] = useState<PlacementSettings>(
    recommendation?.placement_settings ?? {}
  );
  const [startDate, setStartDate] = useState(
    recommendation?.start_date ?? new Date().toISOString().split('T')[0]
  );
  const [durationMode, setDurationMode] = useState<DurationMode>(
    recommendation?.end_date ? 'end_date' : recommendation?.duration_days ? 'duration_days' : 'end_date'
  );
  const [endDate, setEndDate] = useState(recommendation?.end_date ?? '');
  const [durationDays, setDurationDays] = useState(recommendation?.duration_days?.toString() ?? '');
  const [targetMode, setTargetMode] = useState<RecommendationTargetMode>(
    recommendation?.target_mode ?? 'pregnancy'
  );
  const [sswFrom, setSswFrom] = useState(recommendation?.ssw_from?.toString() ?? '');
  const [sswTo, setSswTo] = useState(recommendation?.ssw_to?.toString() ?? '');
  const [babyAgeFrom, setBabyAgeFrom] = useState(recommendation?.baby_age_from_months?.toString() ?? '');
  const [babyAgeTo, setBabyAgeTo] = useState(recommendation?.baby_age_to_months?.toString() ?? '');
  const [visibleToPremium, setVisibleToPremium] = useState(recommendation?.visible_to_premium ?? false);
  const [priority, setPriority] = useState(recommendation?.priority?.toString() ?? '0');
  const [isActive, setIsActive] = useState(recommendation?.is_active ?? true);

  const togglePlacement = (p: RecommendationPlacement) => {
    setPlacements((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
    setPlacementSettings((prev) => {
      if (prev[p]) return prev;
      return { ...prev, [p]: { ...PLACEMENT_DEFAULTS[p] } };
    });
  };

  const getSetting = (p: RecommendationPlacement): PlacementSetting =>
    placementSettings[p] ?? PLACEMENT_DEFAULTS[p];

  const updateSetting = (p: RecommendationPlacement, patch: Partial<PlacementSetting>) => {
    setPlacementSettings((prev) => ({
      ...prev,
      [p]: { ...getSetting(p), ...patch },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (placements.length === 0) {
      alert('Bitte mindestens eine Platzierung auswählen');
      return;
    }
    if (!ctaUrl) {
      alert('CTA-URL ist erforderlich');
      return;
    }
    if (durationMode === 'end_date' && !endDate) {
      alert('Enddatum erforderlich oder auf Dauer umschalten');
      return;
    }
    if (durationMode === 'duration_days' && !durationDays) {
      alert('Dauer erforderlich oder auf Enddatum umschalten');
      return;
    }

    // Only include settings for currently selected placements
    const settingsForSelected: PlacementSettings = {};
    for (const p of placements) {
      settingsForSelected[p] = getSetting(p);
    }

    onSubmit({
      title,
      description: description || null,
      cta_label: ctaLabel || null,
      cta_url: ctaUrl,
      image_url: imageUrl || null,
      show_image: showImage,
      placements,
      start_date: startDate,
      end_date: durationMode === 'end_date' ? endDate : null,
      duration_days: durationMode === 'duration_days' ? parseInt(durationDays, 10) : null,
      target_mode: targetMode,
      ssw_from: targetMode === 'pregnancy' && sswFrom ? parseInt(sswFrom, 10) : null,
      ssw_to: targetMode === 'pregnancy' && sswTo ? parseInt(sswTo, 10) : null,
      baby_age_from_months: targetMode === 'baby' && babyAgeFrom ? parseInt(babyAgeFrom, 10) : null,
      baby_age_to_months: targetMode === 'baby' && babyAgeTo ? parseInt(babyAgeTo, 10) : null,
      visible_to_premium: visibleToPremium,
      priority: parseInt(priority, 10) || 0,
      label: label.trim() || 'Anzeige',
      placement_settings: settingsForSelected,
      is_active: isActive,
    });
  };

  const inputClass =
    'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

  return (
    <form onSubmit={handleSubmit} className="bg-teal-50 p-6 rounded-lg border border-teal-100 space-y-5">
      <h3 className="font-semibold text-slate-900">
        {isEditing ? 'Empfehlung bearbeiten' : 'Neue Empfehlung'}
      </h3>

      {/* Inhalt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Titel *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className={inputClass}
            placeholder="z.B. Bio-Babyschlafsack"
          />
        </div>
        <div>
          <label className={labelClass}>CTA-Label</label>
          <input
            type="text"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            className={inputClass}
            placeholder="Mehr erfahren"
          />
        </div>
        <div>
          <label className={labelClass}>Anzeigen-Label (Badge)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className={inputClass}
            placeholder="Anzeige"
            maxLength={30}
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Wird oben links auf der Karte angezeigt (z.B. &quot;Anzeige&quot;, &quot;Empfehlung&quot;, &quot;Partner&quot;).
          </p>
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Beschreibung</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={inputClass}
            placeholder="Kurze, persönlich wirkende Empfehlung..."
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>CTA-URL *</label>
          <input
            type="url"
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
            required
            className={inputClass}
            placeholder="https://partner.example.com/produkt"
          />
        </div>
      </div>

      {/* Bild */}
      <div>
        <ImageUpload
          value={imageUrl}
          onChange={setImageUrl}
          bucket="recommendation-images"
          label="Bild (optional)"
          placeholder="https://example.com/bild.jpg"
        />
        {imageUrl && (
          <label className="flex items-center gap-2 mt-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showImage}
              onChange={(e) => setShowImage(e.target.checked)}
              className="w-4 h-4 text-teal-500 rounded"
            />
            Bild anzeigen (wenn Platzierung dies unterstützt)
          </label>
        )}
      </div>

      {/* Platzierungen */}
      <div>
        <label className={labelClass}>Platzierungen *</label>
        <div className="space-y-2 mt-1">
          {PLACEMENT_OPTIONS.map((p) => {
            const selected = placements.includes(p);
            const setting = getSetting(p);
            return (
              <div
                key={p}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden"
              >
                <label className="flex items-center gap-2 text-sm text-slate-700 px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => togglePlacement(p)}
                    className="w-4 h-4 text-teal-500 rounded"
                  />
                  <span className="font-medium">{PLACEMENT_LABELS[p]}</span>
                </label>
                {selected && (
                  <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-slate-100">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">Design</label>
                      <select
                        value={setting.variant}
                        onChange={(e) => updateSetting(p, { variant: e.target.value as RecommendationVariant })}
                        className={inputClass}
                      >
                        <option value="compact">{VARIANT_LABELS.compact}</option>
                        <option value="extended">{VARIANT_LABELS.extended}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">Position</label>
                      <select
                        value={setting.position}
                        onChange={(e) => updateSetting(p, { position: e.target.value as RecommendationPosition })}
                        className={inputClass}
                      >
                        {POSITION_OPTIONS_BY_PLACEMENT[p].map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Planung */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Startdatum *</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className={inputClass}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Ende</label>
          <div className="flex items-center gap-4 mb-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={durationMode === 'end_date'}
                onChange={() => setDurationMode('end_date')}
              />
              Enddatum
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={durationMode === 'duration_days'}
                onChange={() => setDurationMode('duration_days')}
              />
              Dauer in Tagen
            </label>
          </div>
          {durationMode === 'end_date' ? (
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputClass}
            />
          ) : (
            <input
              type="number"
              min={1}
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              className={inputClass}
              placeholder="z.B. 30"
            />
          )}
        </div>
      </div>

      {/* Targeting */}
      <div className="space-y-3">
        <div>
          <label className={labelClass}>Zielmodus *</label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={targetMode === 'pregnancy'}
                onChange={() => setTargetMode('pregnancy')}
              />
              Schwangerschaft
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={targetMode === 'baby'}
                onChange={() => setTargetMode('baby')}
              />
              Baby
            </label>
          </div>
        </div>
        {targetMode === 'pregnancy' ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>SSW von (1–42, leer = offen)</label>
              <input
                type="number"
                min={1}
                max={42}
                value={sswFrom}
                onChange={(e) => setSswFrom(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>SSW bis (1–42, leer = offen)</label>
              <input
                type="number"
                min={1}
                max={42}
                value={sswTo}
                onChange={(e) => setSswTo(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Baby-Alter von (Monate, leer = offen)</label>
              <input
                type="number"
                min={0}
                value={babyAgeFrom}
                onChange={(e) => setBabyAgeFrom(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Baby-Alter bis (Monate, leer = offen)</label>
              <input
                type="number"
                min={0}
                value={babyAgeTo}
                onChange={(e) => setBabyAgeTo(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </div>

      {/* Management */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Priorität</label>
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm h-[38px]">
            <input
              type="checkbox"
              checked={visibleToPremium}
              onChange={(e) => setVisibleToPremium(e.target.checked)}
              className="w-4 h-4 text-teal-500 rounded"
            />
            Auch Premium-Nutzern anzeigen
          </label>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm h-[38px]">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-teal-500 rounded"
            />
            Aktiv
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-1"
        >
          <X className="w-4 h-4" />
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center gap-1"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}
